import React, { useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/admin';
import { useHydrantPlanDetailQuery, usePriraditKontroloraMutation } from '../../../api/JsdhNotifikaceService/service/HydrantBodMutations';
import { HydrantBodService } from '../../../api/JsdhNotifikaceService/service/HydrantBodService';
import { HydrantPlanPolozka } from '../../../api/JsdhNotifikaceService/generated/models/HydrantBod';

const stavColor = (stav: HydrantPlanPolozka['stav']): 'default' | 'success' | 'warning' => {
  if (stav === 'DOKONCENO') return 'success';
  if (stav === 'PRESKOCENO') return 'warning';
  return 'default';
};

const stavLabel = (stav: HydrantPlanPolozka['stav']): string => {
  if (stav === 'DOKONCENO') return 'Dokončeno';
  if (stav === 'PRESKOCENO') return 'Přeskočeno';
  return 'Čeká';
};

const HydrantPlanDetailPage: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const id = planId ? Number(planId) : undefined;
  const { data, isLoading } = useHydrantPlanDetailQuery(id);
  const priradit = usePriraditKontroloraMutation();

  const [prirazeniPolozkaId, setPrirazeniPolozkaId] = useState<number | null>(null);
  const [jmeno, setJmeno] = useState('');

  const closeDialog = () => { setPrirazeniPolozkaId(null); setJmeno(''); };

  const handlePriradit = () => {
    if (prirazeniPolozkaId === null) return;
    priradit.mutate(
      { polozkaId: prirazeniPolozkaId, data: { jmeno } },
      { onSuccess: closeDialog },
    );
  };

  if (isLoading || !data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  const { plan, celkemPolozek, dokoncenoPolozek, polozky } = data;
  const pct = celkemPolozek > 0 ? Math.round((dokoncenoPolozek / celkemPolozek) * 100) : 0;

  return (
    <Box>
      <PageHeader
        title={plan.nazev}
        subtitle={`${plan.datumOd} – ${plan.datumDo} · ${plan.stav}`}
        action={
          <Button size="small" onClick={() => HydrantBodService.stahniPlanReportPdf(plan.id)}>
            Souhrn PDF
          </Button>
        }
      />

      <Stack spacing={1} mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{ flex: 1, height: 10, borderRadius: 5 }}
          />
          <Typography variant="body2" noWrap>
            {dokoncenoPolozek}/{celkemPolozek} ({pct} %)
          </Typography>
        </Stack>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Hydrant</TableCell>
            <TableCell>Stav</TableCell>
            <TableCell>Kontrolor</TableCell>
            <TableCell align="right">Akce</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(polozky ?? []).map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.hydrantOznaceni ?? `#${p.hydrantId}`}</TableCell>
              <TableCell>
                <Chip label={stavLabel(p.stav)} color={stavColor(p.stav)} size="small" />
              </TableCell>
              <TableCell>{p.prirazenoJmeno ?? '—'}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setPrirazeniPolozkaId(p.id); setJmeno(p.prirazenoJmeno ?? ''); }}
                >
                  Přiřadit
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={prirazeniPolozkaId !== null}
        onClose={() => { setPrirazeniPolozkaId(null); setJmeno(''); }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Přiřadit kontrolora</DialogTitle>
        <DialogContent>
          <TextField
            label="Jméno kontrolora"
            value={jmeno}
            onChange={(e) => setJmeno(e.target.value)}
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setPrirazeniPolozkaId(null); setJmeno(''); }}>Zrušit</Button>
          <Button variant="contained" disabled={!jmeno || priradit.isPending} onClick={handlePriradit}>
            Uložit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HydrantPlanDetailPage;
